import * as angular from 'angular';
import {IController} from 'angular';
import * as _ from 'lodash';
import {ScheduleDialogService} from '../../../schedule/services/schedule-dialog.service';
import {BatchRequestsService} from '../../../batch/services/batch-requests.service';
import {BatchDialogService} from '../../../batch/services/batch-dialogs-service.service';
import {BatchAlertsService} from '../../../batch/services/batch-alerts.service';
import {BringozLoaderService} from '../../../commons/services/bringoz-loader.service';
import {ConfirmDialogService} from '../../../commons/services/confirm-dialog.service';
import {ConfirmChangeStateService} from '../../../commons/services/confirm-change-state.service';
import {AGENT_SCHEDULE_IMPORTER_PATH} from '../../../schedule/constants/agent-schedule-import.const';
import {PanelService} from '../../../commons/angular/services/panel.service';
import {
  AgentScheduleColumnsSettingsPanelComponent
} from '../../angular/components/agent-schedule-columns-settings-panel/agent-schedule-columns-settings-panel.component';
import {
  AGENT_SCHEDULE_TOGGLEABLE_COLUMNS,
  AgentScheduleColumns
} from '../../angular/components/agent-schedule-import-table/agent-schedule-import-table.constant';

const AGENT_SCHEDULE_ENTITY = 'AgentSchedule';

/**
 * Raw file column values for a row, as documented for AgentScheduleXlsRow. Always populated,
 * even when `entity` is null due to a blocking error - this is what the preview table renders.
 * `date`/`startTime`/`endTime`/`recurringStartDate`/`recurringEndDate` come back as Jackson's
 * array serialization of java.time.LocalDate/LocalTime ([year, month, day] / [hour, minute, ...]).
 */
export interface AgentScheduleRawRow {
  agent?: string;
  name?: string;
  type?: string;
  timezone?: string;
  programType?: string;
  branchIds?: string[];
  serviceAreaIds?: string[];
  date?: number[];
  startTime?: number[];
  endTime?: number[];
  freq?: string;
  recurringStartDate?: number[];
  recurringEndDate?: number[];
  interval?: number;
  byWeekDay?: string[];
  absenceReason?: string;
}

/**
 * Ready-to-render display strings for a row, precomputed once in _enrichRow so the template
 * never reformats or calls a controller method inline.
 */
export interface AgentScheduleRowDisplay {
  agent: string;
  type: string;
  timezone: string;
  name: string;
  programType: string;
  branchIds: string[];
  serviceAreaIds: string[];
  date: string;
  startTime: string;
  endTime: string;
  freq: string;
  recurringStartDate: string;
  recurringEndDate: string;
  interval: string;
  byWeekDay: string[];
  absenceReason: string;
}

/**
 * Row as returned by /importers-agent-schedules/upload - AgentScheduleImportVerification.
 */
export interface AgentScheduleImportRow {
  rowNumber?: number;
  entity?: any;
  rawRow?: AgentScheduleRawRow;
  display?: AgentScheduleRowDisplay;
  mappingErrors?: Array<AgentScheduleMappingError | string>;
  rowId?: string;
  errorsList?: string[];
  hasErrors?: boolean;
}

export interface AgentScheduleMappingError {
  type?: string;
  description?: string;
  message?: string;
  fieldName?: string;
}

export class AgentScheduleImportViewCtrl implements IController {

  public entity: string = AGENT_SCHEDULE_ENTITY;
  public rows: AgentScheduleImportRow[] = [];
  public visibleRows: AgentScheduleImportRow[] = [];
  public totals: {total: number, errors: number} = {total: 0, errors: 0};
  public currentNavItem: string = 'total';
  public uploadComplete: boolean = false;
  public isFieldMappingOpen: boolean = false;
  public schemas: any[] = [];
  public schemaDefaultFields: any[] = [];
  public columns: AgentScheduleColumns = _.mapValues(AGENT_SCHEDULE_TOGGLEABLE_COLUMNS, (column) => ({...column}));

  /* @ngInject */
  constructor(
    private $state,
    private $log,
    private $q,
    private UUIDService,
    private scheduleDialogService: ScheduleDialogService,
    private batchRequestsService: BatchRequestsService,
    private batchDialogService: BatchDialogService,
    private batchAlertsService: BatchAlertsService,
    private bringozLoaderService: BringozLoaderService,
    private confirmDialogService: ConfirmDialogService,
    private confirmChangeStateService: ConfirmChangeStateService,
    private panelService: PanelService
  ) {
  }

  $onInit() {
    this._loadSchemas();
  }

  changeNavItem(item: string) {
    this.currentNavItem = item;
    this._applyRows(this.rows);
  }

  onUpload(event: Event) {
    this.scheduleDialogService
      .showUploadAgentSchedulesDialog(event, this.schemas)
      .then((rows) => {
        this.currentNavItem = 'total';
        this._applyRows(_.map(rows || [], (row) => this._enrichRow(row)));
        this.confirmChangeStateService.setIsEnabled(this.uploadComplete);
      }, () => {
      })
      .catch((error) => {
        this.batchAlertsService.getEntityImportPreviewError();
        this.$log.error('Failed to process agent schedules upload response', error);
      });
  }

  onRemoveRow(row: AgentScheduleImportRow) {
    this._applyRows(_.reject(this.rows, (r) => r.rowId === row.rowId));

    if (!this.rows.length) {
      this.currentNavItem = 'total';
      this.confirmChangeStateService.reset();
    }
  }

  onCancel(event: Event) {
    return this.confirmDialogService
      .confirm('Cancel Upload', 'Are you sure you want to cancel the upload?', event)
      .then(() => {
        this._reset();
      })
      .catch(() => {
        this.$log.info('Dialog Closed');
      });
  }

  onImport(event: Event) {
    // Rows the server flagged cannot be imported, so only clean rows are sent.
    const items = _.chain(this.rows)
      .reject((row) => row.hasErrors)
      .map((row) => _.omit(row, ['rowId', 'errorsList', 'hasErrors', 'display']))
      .value();

    if (!items.length) {
      return;
    }

    this.bringozLoaderService.on();

    this.batchRequestsService.createAgentAssignmentsBatch(AGENT_SCHEDULE_IMPORTER_PATH, items)
      .then((batch) => {
        this._showImportProgress(event, batch);
      })
      .catch((error) => {
        this.batchAlertsService.importEntitiesError(error);
        this.$log.error('Failed to import agent schedules', error);
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });
  }

  onOpenColumnSettings(event: Event) {
    const panel = this.panelService.open(AgentScheduleColumnsSettingsPanelComponent, event, {columns: this.columns}, {
      position: {originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 8},
      width: 320
    });

    panel.afterClose$.subscribe((columns: AgentScheduleColumns | null) => {
      if (!columns) {
        return;
      }

      this.columns = columns;
    });
  }

  onOpenFieldMappingPanel() {
    this.isFieldMappingOpen = true;
  }

  onCloseFieldMappingPanel() {
    this.isFieldMappingOpen = false;
  }

  onSchemaCreated(schema) {
    this.schemas = [...this.schemas, schema];
  }

  onSchemaUpdated(schema) {
    this.schemas = _.map(this.schemas, (s) => s.id === schema.id ? schema : s);
  }

  onSchemaDeleted(schemaId) {
    this.schemas = _.reject(this.schemas, (s) => s.id === schemaId);
  }

  // ******************************
  //        Private methods
  // ******************************

  private _loadSchemas() {
    // batchRequestsService returns native promises - $q.all adopts them so the results
    // land inside a digest. A failure must not block uploading, it only means no saved
    // mapping can be picked, hence the per-call catch.
    this.$q.all([
      this.$q.when(this.batchRequestsService.getSchemas(AGENT_SCHEDULE_ENTITY)).catch(() => []),
      this.$q.when(this.batchRequestsService.getSchemaDefaultFields(AGENT_SCHEDULE_ENTITY)).catch(() => [])
    ])
      .then(([schemas, schemaDefaultFields]: any[]) => {
        this.schemas = schemas || [];
        this.schemaDefaultFields = schemaDefaultFields || [];
      });
  }

  private _showImportProgress(event: Event, batch) {
    this.batchDialogService.batchAgentAssignmentsProgress(event, batch, AGENT_SCHEDULE_IMPORTER_PATH, 'Agent Schedules')
      .then((errorItems: any) => {
        this._applyBatchErrors(errorItems || []);
      })
      .catch(() => {
        this.$log.info('Dialog Closed');
      });
  }

  private _applyRows(rows: AgentScheduleImportRow[]) {
    this.rows = rows;
    this.totals = this._getTotals(rows);
    this.uploadComplete = !!rows.length;
    this.visibleRows = this.currentNavItem === 'errors'
      ? _.filter(rows, (row) => row.hasErrors)
      : rows;
  }

  private _enrichRow(row: AgentScheduleImportRow): AgentScheduleImportRow {
    const mappingErrors = row.mappingErrors || [];
    const errorsList = this._formatErrorsList(mappingErrors);

    return {
      ...row,
      rowId: this.UUIDService.generate(),
      hasErrors: _.some(mappingErrors, (error) => this._isBlockingError(error)),
      errorsList,
      display: this._buildDisplay(row.rawRow || {})
    };
  }

  private _buildDisplay(rawRow: AgentScheduleRawRow): AgentScheduleRowDisplay {
    return {
      agent: rawRow.agent || '-',
      type: rawRow.type || '-',
      timezone: rawRow.timezone || '-',
      name: rawRow.name || '-',
      programType: rawRow.programType || '-',
      branchIds: rawRow.branchIds || [],
      serviceAreaIds: rawRow.serviceAreaIds || [],
      date: this._formatDate(rawRow.date),
      startTime: this._formatTime(rawRow.startTime),
      endTime: this._formatTime(rawRow.endTime),
      freq: rawRow.freq || '-',
      recurringStartDate: this._formatDate(rawRow.recurringStartDate),
      recurringEndDate: this._formatDate(rawRow.recurringEndDate),
      interval: _.isNumber(rawRow.interval) ? String(rawRow.interval) : '-',
      byWeekDay: rawRow.byWeekDay || [],
      absenceReason: rawRow.absenceReason || '-'
    };
  }

  // rawRow date/time fields are Jackson's array serialization of java.time.LocalDate/LocalTime
  // ([year, month, day] / [hour, minute, ...]), not ISO strings - format them explicitly.
  private _formatDate(value: number[]): string {
    if (!_.isArray(value) || value.length < 3) {
      return '-';
    }

    const [year, month, day] = value;
    return `${year}-${_.padStart(String(month), 2, '0')}-${_.padStart(String(day), 2, '0')}`;
  }

  private _formatTime(value: number[]): string {
    if (!_.isArray(value) || value.length < 2) {
      return '-';
    }

    const [hour, minute] = value;
    return `${_.padStart(String(hour), 2, '0')}:${_.padStart(String(minute), 2, '0')}`;
  }

  private _formatErrorsList(mappingErrors: Array<AgentScheduleMappingError | string>): string[] {
    return _.chain(mappingErrors)
      .map((error) => _.isString(error) ? error : (error?.description || error?.message || error?.fieldName))
      .compact()
      .uniq()
      .value();
  }

  private _isBlockingError(error: AgentScheduleMappingError | string): boolean {
    if (_.isString(error)) {
      return true;
    }

    const type = _.get(error, 'type');
    return !type || type === 'Error';
  }

  private _getTotals(rows: AgentScheduleImportRow[]) {
    return {
      total: rows.length,
      errors: _.filter(rows, (row) => row.hasErrors).length
    };
  }

  private _applyBatchErrors(errorItems: any[]) {
    if (!errorItems.length) {
      this.batchAlertsService.importEntitiesSuccess();
      this._reset();
      this.$state.go('agents-schedule-beta');
      return;
    }

    const errorRowNumbers = _.map(errorItems, 'rowNumber');

    const failedRows = _.chain(this.rows)
      .filter((row) => _.includes(errorRowNumbers, row.rowNumber))
      .map((row) => {
        const errorItem = _.find(errorItems, (item) => item.rowNumber === row.rowNumber);
        return this._enrichRow({
          ...row,
          mappingErrors: _.get(errorItem, 'mappingErrors', [])
        });
      })
      .value();

    this.currentNavItem = 'errors';
    this._applyRows(failedRows);
  }

  private _reset() {
    this.currentNavItem = 'total';
    this._applyRows([]);
    this.confirmChangeStateService.reset();
  }
}

angular
  .module('bringoz.admin.agents')
  .component('agentScheduleImportView', {
    bindings: {},
    template: require('./agent-schedule-import-view.html').default,
    controller: AgentScheduleImportViewCtrl
  });
